<template>
  <div class="board">
    <table class="board-table" role="table">
      <colgroup>
        <col style="width: 96px" />
        <col />
        <col style="width: 160px" />
        <col style="width: 160px" />
        <col style="width: 100px" />
      </colgroup>

      <thead>
        <tr>
          <th scope="col">번호</th>
          <th scope="col">제목</th>
          <th scope="col">작성자</th>
          <th scope="col">등록일</th>
          <th scope="col">조회수</th>
        </tr>
      </thead>

      <tbody>

        <tr v-for="(vo, index) in expertList" :key="index">
          <td v-if="vo.pinned"><span class="notice-badge">공지사항</span></td>
          <td v-else>{{ vo.no }}</td>
          <td class="cell-title" @click="goToDetail(vo.expertId)">
            <span class="title-text">{{ vo.title }}</span>
            <i class="fa-regular fa-file-lines file-mark" aria-hidden="true"></i>
          </td>
          <td @click="goToDetail(vo.expertId)">{{ vo.adminName }}</td>
          <td>{{ vo.updatedAt.slice(0, 10).replace('T', ' ') }}</td>
          <td>{{ vo.viewCount }}</td>
        </tr>
      </tbody>
    </table>

    <div class="pagination-wrapper">
      <Pagination id="pagination" class="pagination" v-if="userJson.pagination" :gridInfo="userJson"
        @getGridData="getGridData" />
    </div>
  </div>

    <!-- ✅ 검색창 섹션 (커스텀 셀렉트 + 입력 + 버튼) -->
  <section class="search-section" aria-label="교육 검색">
    <div class="search-box">
      <!-- 커스텀 셀렉트: 라디오를 v-model로 묶어서 기존 searchCode 그대로 사용 -->
      <details class="select-custom" role="listbox" @toggle="selectOpen = $event.target.open">
        <summary aria-label="검색 구분 선택">
          <span class="selected-text" aria-live="polite"></span>
          <i class="fa-solid fa-caret-down" aria-hidden="true"></i>
        </summary>

        <div class="options" role="listbox">
          <input type="radio" name="searchScope" id="scope-all" value="all" v-model="searchCode" />
          <label role="option" :aria-selected="searchCode === 'all' ? 'true' : 'false'" data-label="전체" for="scope-all"
            @click="closeSelect">전체</label>

          <input type="radio" name="searchScope" id="scope-title" value="title" v-model="searchCode" />
          <label role="option" :aria-selected="searchCode === 'title' ? 'true' : 'false'" data-label="제목" for="scope-title"
            @click="closeSelect">제목</label>

          <!-- '제목 + 내용'은 기존 로직에서 all과 동일하게 처리 -->
          <input type="radio" name="searchScope" id="scope-title-content" value="all" v-model="searchCode" />
          <label role="option" :aria-selected="searchCode === 'all' ? 'true' : 'false'" data-label="제목 + 내용"
            for="scope-title-content" @click="closeSelect">제목 + 내용</label>
        </div>
      </details>

      <!-- 검색어 입력/버튼: 기존 로직 그대로 -->
      <input type="text" class="search-input" placeholder="검색어를 입력하세요." aria-label="검색어 입력" v-model.trim="searchText"
        @keyup.enter="handleSearch" />
      <button class="search-btn" @click="handleSearch">
        <i class="fa-solid fa-magnifying-glass" aria-hidden="true"></i> 검색
      </button>
    </div>
  </section>
</template>

<script setup>
import { useRouter } from 'vue-router';
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex';
import Pagination from '@/components/common/grid/pagination.vue'
import userJson from '@/models/gridInfo/user.json'

const store = useStore()
const router = useRouter()
const searchText = ref('')
const searchCode = ref('all')
const selectOpen = ref(false)

const searchParam = computed(() => {
  return store.state.temp.searchParam
})

const expertList = computed(() => store.state.grid.expertData)

const getGridData = async () => {
  const param = {
    pageNo: searchParam.value.pageNo,
    countPerRecord: searchParam.value.countPerRecord,
    search: searchText.value,
    typeCd: searchCode.value
  }
  await store.dispatch('grid/expertList', param)
}

const handleSearch = async () => {
  await getGridData()
}

const closeSelect = (e) => {
  const root = e?.target?.closest('details')
  if (root) root.open = false
}

const goToDetail = (code) => {
  router.push({
    name: 'expertdetail',
    query: {
      expertId: code
    }
  })
}

onMounted(async () => {
  await getGridData()
})

onUnmounted(() => {
  store.commit('temp/clearSearchParam')

}) 
</script>

<style scoped>
.board {
  max-width: 1200px;
  margin: 0px auto 20px;
}

.board-table {
  width: 100%;
  table-layout: fixed;
}

.board-table th,
.board-table td {
  padding: 14px 16px;
  border-bottom: 1px solid #e9ecef;
  font-size: 14px;
  line-height: 1.4;
}

.board-table thead th {
  background: #e5e7eb;
  color: #0f172a;
  font-weight: 600;
  text-align: center;
}

.board-table tbody td {
  color: #111827;
  text-align: center;
}

.notice-badge {
  display: inline-block;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  color: #111827;
  /* 거의 블랙 텍스트 */
  background: #e5e7eb;
  /* 밝은 회색 배경 */
  border-radius: 12px;
  /* pill 형태 */
  white-space: nowrap;
}


.board-table .cell-title {
  text-align: left;
  cursor: pointer;
  max-width: 400px; /* ✅ 필요에 따라 조정 (테이블 폭에 맞게) */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
}

/* ✅ 제목 텍스트만 말줄임 적용 (아이콘은 제외) */
.board-table .cell-title .title-text {
  display: inline-block;
  max-width: calc(100% - 20px); /* 아이콘 영역 확보 */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
}

/* 제목 옆 작은 문서 아이콘 */
.board-table .file-mark {
  margin-left: 6px;
  font-size: 12px;
  color: #9ca3af;
  vertical-align: middle;
}

/* 행 호버 효과 */
.board-table tbody tr:hover {
  background: #f9fbff;
}

/* 모바일: 컬럼 축약 (작성자/조회수 숨김) */
@media (max-width: 768px) {

  .board-table th:nth-child(3),
  .board-table td:nth-child(3),
  .board-table th:nth-child(5),
  .board-table td:nth-child(5) {
    display: none;
  }

  .board-table colgroup col:nth-child(3),
  .board-table colgroup col:nth-child(5) {
    display: none;
  }
}

.board {
  max-width: 1200px;
  margin: 0 auto 20px;
  display: flex;
  /* 세로 배치 */
  flex-direction: column;
}

.pagination-wrapper {
  display: flex;
  justify-content: center;
  /* 가로 가운데 */
  align-items: center;
  padding: 16px 0 0;
  /* 테이블과 간격 */
}


/* 검색 영역 레이아웃 */
.search-section {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0px 0 80px;
}

.search-box {
  display: grid;
  grid-template-columns: 160px 1fr auto;
  gap: 10px;
  max-width: 620px;
  width: 100%;
}


/* 레이아웃은 grid */
.search-section .search-box {
  display: grid;
  grid-template-columns: 200px 1fr auto;
  gap: 10px;
  align-items: center;
}

/* 타이틀 규격 (필요 시 섹션 제목에 적용) */
.search-section .title {
  font-size: 1.625rem;
  font-weight: 700;
}

/* 설명글(p) 규격 */
.search-section p {
  font-size: 0.95rem;
}

/* 반응형 */
@media (max-width: 600px) {
  .search-section .search-box {
    grid-template-columns: 1fr;
  }
}

/* ============ 커스텀 셀렉트 ============ */
.select-custom {
  /* 기본값(라디오 선택에 따라 바뀜) */
  --selected: "전체";
  position: relative;
  width: 100%;
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 0.95rem;
}

/* 요약(닫힘 상태 보이는 칸) */
.select-custom>summary {
  list-style: none;
  /* 기본 삼각형 제거 */
  display: flex;
  justify-content: space-between;
  /* 텍스트/아이콘 좌우 중앙정렬 */
  align-items: center;
  /* 수직 중앙정렬 */
  padding: 10px 12px;
  border: 1px solid #ccc;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  user-select: none;
  outline: none;
}

/* 포커스/호버 */
.select-custom>summary:focus-visible,
.select-custom>summary:hover {
  border-color: #1d2088;
}

/* 열렸을 때 아래 상자와 시각적으로 이어지도록 하단 둥근 모서리 제거 */
.select-custom[open]>summary {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

/* ▼ 아이콘 */
.select-custom>summary .fa-caret-down {
  font-size: 0.9rem;
  color: #555;
}

/* 선택된 텍스트: CSS 변수로 반영 */
.select-custom .selected-text::after {
  content: var(--selected);
  display: inline-block;
  line-height: 1.4;
}

/* 옵션 상자 */
.select-custom .options {
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% - 1px);
  /* 테두리 겹치게 해서 '아래 라인 끊김' 방지 */
  border: 1px solid #ccc;
  border-top: none;
  /* summary 테두리와 이어지게 */
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  background: #fff;
  box-shadow: 0 6px 14px rgba(0, 0, 0, .08);
  z-index: 30;
}

/* 네이티브 input 숨기기 */
.select-custom .options input[type="radio"] {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

/* 옵션 라벨 스타일 */
.select-custom .options label {
  display: block;
  padding: 10px 12px;
  cursor: pointer;
  white-space: nowrap;
  font-size: 0.95rem;
}

/* 호버/포커스 */
.select-custom .options label:hover,
.select-custom .options label:focus {
  background: #f3f4f6;
}

/* 접근성: 현재 선택 항목 강조(라디오 checked 근처 라벨에 스타일) */
.select-custom:has(#scope-all:checked) label[for="scope-all"],
.select-custom:has(#scope-title:checked) label[for="scope-title"],
.select-custom:has(#scope-title-content:checked) label[for="scope-title-content"] {
  background: #eef2ff;
  color: #1d2088;
  font-weight: 700;
}

/* ✅ 선택값을 summary에 반영 (JS 없이) */
.select-custom:has(#scope-all:checked) {
  --selected: "전체";
}

.select-custom:has(#scope-title:checked) {
  --selected: "제목";
}

.select-custom:has(#scope-title-content:checked) {
  --selected: "제목 + 내용";
}

/* ============ 검색 인풋 + 버튼 ============ */
.search-input {
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 0.95rem;
  padding: 10px 14px;
  border: 1px solid #ccc;
  border-radius: 8px;
  outline: none;
  transition: border-color 0.2s ease;
}

.search-input:focus {
  border-color: #1d2088;
}

.search-btn {
  background-color: #1d2088;
  color: #fff;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.25s ease;
}

.search-btn:hover {
  background-color: #141664;
}

/* 반응형(선택) */
@media (max-width: 600px) {
  .search-box {
    grid-template-columns: 1fr;
  }
}
</style>
